(() => {
  'use strict';
  if (window.__tpCartRecoveryCapture) return;
  window.__tpCartRecoveryCapture = { version: '2026-10-22', status: 'ready' };
  const loader = document.querySelector('script[data-tp-cart-capture]');
  if (loader) loader.dataset.captureState = 'ready';
  const forms = { WcyHAt: 'ES', VM9DGg: 'EN' };
  const snapshots = new Map();
  const submissions = new Map();

  function imageUrl(item) {
    const source = item.featured_image?.url || item.image;
    if (typeof source !== 'string' || !source.trim()) return '';
    try {
      const url = new URL(source, 'https://trendsplant.com');
      if (url.protocol === 'http:') url.protocol = 'https:';
      return url.protocol === 'https:' ? url.href : '';
    } catch (_) {
      return '';
    }
  }

  async function readCart() {
    const root = window.Shopify?.routes?.root || '/';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(root + 'cart.js', {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart unavailable');
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  async function save(properties, captured) {
    const { email, ...request } = properties;
    const profile = { type: 'profile', attributes: { email, properties: request } };
    const data = captured ? {
      type: 'event', attributes: {
        unique_id: properties.TP_CartRecoveryID,
        time: properties.TP_CartRecoveryRequestedAt,
        properties: {
          RequestID: request.TP_CartRecoveryID, FormID: request.TP_CartRecoveryForm,
          RequestedAt: request.TP_CartRecoveryRequestedAt, Lang: request.Lang,
          CartURL: request.TP_CartRecoveryURL, Items: request.TP_CartRecoveryItems,
          Summary: request.TP_CartRecoverySummary
        },
        metric: { data: { type: 'metric', attributes: { name: 'TP Cart Recovery Requested' } } },
        profile: { data: profile }
      }
    } : profile;
    const body = JSON.stringify({ data });
    if (loader) loader.dataset.captureState = 'saving';
    const response = await fetch(`https://a.klaviyo.com/client/${captured ? 'events' : 'profiles'}?company_id=RTFeLq`, {
      method: 'POST', credentials: 'omit',
      headers: { 'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json', revision: captured ? '2026-10-15' : '2026-07-15' },
      body, keepalive: new TextEncoder().encode(body).byteLength < 60000
    });
    if (!response.ok) throw new Error('Cart capture was not accepted');
  }

  window.addEventListener('klaviyoForms', async (event) => {
    const detail = event.detail || {};
    if (loader) loader.dataset.lastFormEvent = `${detail.type}:${detail.formId}`;
    const lang = forms[detail.formId];
    if (!lang) return;
    if (detail.type === 'open') {
      const promise = readCart().catch(() => null);
      snapshots.set(detail.formId, { at: Date.now(), promise });
      submissions.delete(detail.formId);
      return;
    }
    if (detail.type !== 'submit' && detail.type !== 'stepSubmit') return;
    const rawEmail = detail.metaData?.email ?? detail.metaData?.$email;
    if (typeof rawEmail !== 'string') return;
    const email = rawEmail.trim();
    if (!email.includes('@')) return;
    if (submissions.get(detail.formId)?.email === email.toLowerCase()) return;
    submissions.set(detail.formId, { email: email.toLowerCase() });
    const properties = {
      email, Lang: lang,
      TP_CartRecoveryID: window.crypto.randomUUID(),
      TP_CartRecoveryForm: detail.formId,
      TP_CartRecoveryRequestedAt: new Date().toISOString(),
      TP_CartRecoveryURL: '', TP_CartRecoverySummary: '', TP_CartRecoveryItems: [],
      TP_CartRecoveryStatus: 'unavailable'
    };
    let captured = false;
    if (loader) loader.dataset.captureState = 'reading-cart';
    try {
      const snapshot = snapshots.get(detail.formId);
      const cart = await readCart().catch(() =>
        snapshot && Date.now() - snapshot.at < 30000 ? snapshot.promise : null
      );
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const unsupported = items.some(item => item.selling_plan_allocation ||
        Object.values(item.properties || {}).some(value => value !== '' && value != null));
      if (!items.length || unsupported) throw new Error('Unsupported or empty cart');
      const quantities = new Map();
      for (const item of items) {
        const variant = String(item.variant_id);
        const quantity = Number(item.quantity);
        if (!/^\d+$/.test(variant) || !Number.isSafeInteger(quantity) || quantity < 1)
          throw new Error('Invalid cart item');
        const total = (quantities.get(variant) || 0) + quantity;
        if (!Number.isSafeInteger(total)) throw new Error('Invalid cart quantity');
        quantities.set(variant, total);
      }
      const path = [...quantities].map(([id, qty]) => `${id}:${qty}`).join(',');
      const url = new URL(`/cart/${path}`, 'https://trendsplant.com');
      url.searchParams.set('storefront', 'true');
      url.searchParams.set('locale', lang === 'ES' ? 'es' : 'en');
      properties.TP_CartRecoveryURL = url.href;
      properties.TP_CartRecoverySummary = items.map(item =>
        `${item.quantity} × ${item.product_title || item.title}${item.variant_title && item.variant_title !== 'Default Title' ? ' — ' + item.variant_title : ''}`
      ).join(' · ');
      properties.TP_CartRecoveryItems = items.map(item => ({
        variant_id: String(item.variant_id),
        title: String(item.product_title || item.title || ''),
        variant: item.variant_title && item.variant_title !== 'Default Title'
          ? String(item.variant_title) : '',
        quantity: Number(item.quantity),
        image_url: imageUrl(item)
      }));
      properties.TP_CartRecoveryStatus = 'captured';
      captured = true;
    } catch (_) {
      properties.TP_CartRecoveryStatus = 'unavailable';
    }
    snapshots.delete(detail.formId);
    try {
      await save(properties, captured);
      window.__tpCartRecoveryCapture.status = captured ? 'captured' : 'unavailable';
    } catch (_) {
      submissions.delete(detail.formId);
      window.__tpCartRecoveryCapture.status = 'delivery-failed';
    }
    if (loader) loader.dataset.captureState = window.__tpCartRecoveryCapture.status;
  });
})();
